import { useEffect, useRef } from 'react';

interface SchemaOrgProps {
  type: 'LocalBusiness' | 'FAQPage' | 'WebPage';
  data: Record<string, unknown>;
}

let schemaCounter = 0;

export function useSchemaOrg({ type, data }: SchemaOrgProps) {
  const idRef = useRef<string>('');
  
  if (!idRef.current) {
    schemaCounter += 1;
    idRef.current = `schema-${type.toLowerCase()}-${schemaCounter}`;
  }

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pianomovers.co.uk';
    
    let schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    };

    // Replace placeholder URLs with actual site URL
    const schemaString = JSON.stringify(schema).replace(/https:\/\/pianomovers\.co\.uk/g, siteUrl);
    schema = JSON.parse(schemaString);

    const scriptId = idRef.current;
    let scriptTag = document.getElementById(scriptId);

    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
}

export function usePageMeta(
  title: string,
  description: string,
  keywords?: string,
  canonical?: string,
  ogTitle?: string,
  ogDescription?: string,
  twitterTitle?: string,
  twitterDescription?: string
) {
  useEffect(() => {
    document.title = title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // Canonical URL
    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    }

    // Open Graph
    let ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (!ogTitleTag) {
      ogTitleTag = document.createElement('meta');
      ogTitleTag.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitleTag);
    }
    ogTitleTag.setAttribute('content', ogTitle || title);

    let ogDescriptionTag = document.querySelector('meta[property="og:description"]');
    if (!ogDescriptionTag) {
      ogDescriptionTag = document.createElement('meta');
      ogDescriptionTag.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescriptionTag);
    }
    ogDescriptionTag.setAttribute('content', ogDescription || description);

    if (canonical) {
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute('content', canonical);
    }

    // Twitter Card
    let twitterTitleTag = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitleTag) {
      twitterTitleTag = document.createElement('meta');
      twitterTitleTag.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitleTag);
    }
    twitterTitleTag.setAttribute('content', twitterTitle || ogTitle || title);

    let twitterDescriptionTag = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDescriptionTag) {
      twitterDescriptionTag = document.createElement('meta');
      twitterDescriptionTag.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDescriptionTag);
    }
    twitterDescriptionTag.setAttribute('content', twitterDescription || ogDescription || description);

    if (canonical) {
      let twitterUrl = document.querySelector('meta[name="twitter:url"]');
      if (!twitterUrl) {
        twitterUrl = document.createElement('meta');
        twitterUrl.setAttribute('name', 'twitter:url');
        document.head.appendChild(twitterUrl);
      }
      twitterUrl.setAttribute('content', canonical);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, twitterTitle, twitterDescription]);
}